import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";
import CompanySlidePanelContent from "./CompanySlidePanelContent";

const ANIMATION_MS = 320;

const CompanySlidePanel = ({ open, companyId, onClose }) => {
  const panelRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (open) {
      setShowContent(false);
      setIsRendered(true);
      document.body.style.overflow = "hidden";

      const frame = requestAnimationFrame(() => {
        setIsActive(true);
      });

      return () => cancelAnimationFrame(frame);
    }

    setShowContent(false);
    setIsActive(false);
    document.body.style.overflow = "";

    const timer = window.setTimeout(() => setIsRendered(false), ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!isActive || !open || !panelRef.current) return undefined;

    const node = panelRef.current;

    const revealContent = () => {
      setShowContent(true);
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== node || event.propertyName !== "transform") return;
      revealContent();
    };

    const fallback = window.setTimeout(revealContent, ANIMATION_MS + 16);
    node.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      window.clearTimeout(fallback);
      node.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isActive, open]);

  if (!isRendered) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`companySlideBackdrop ${isActive ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`companySlidePanel ${isActive ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Company"
      >
        <div className="companySlidePanelHeader">
          <IconButton
            onClick={onClose}
            aria-label="Close"
            className="companySlidePanelClose"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <div className="companySlidePanelContent">
          {showContent && companyId ? (
            <CompanySlidePanelContent companyId={companyId} />
          ) : (
            <div className="companySlidePanelLoader">
              <CircularProgress size={28} />
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default CompanySlidePanel;
